import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, PlusCircle, Images, Users, CheckSquare,
  QrCode, BarChart3, CreditCard, Settings as SettingsIcon, Bell, LogOut, Menu, X,
} from "lucide-react";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/events", label: "My Events", icon: CalendarDays },
  { to: "/app/events/new", label: "Create Event", icon: PlusCircle },
  { to: "/app/photos", label: "Photos", icon: Images },
  { to: "/app/clients", label: "Clients", icon: Users },
  { to: "/app/selections", label: "Selections", icon: CheckSquare },
  { to: "/app/qr", label: "QR Codes", icon: QrCode },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/app/subscription", label: "Subscription", icon: CreditCard },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const [showNotif, setShowNotif] = useState(false);

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); }, []);

  const doLogout = () => { logout(); navigate("/"); };
  const markRead = async () => { await api.post("/notifications/read-all").catch(() => {}); loadNotifs(); };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6"><Logo /></div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto no-scrollbar">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.replace(/\s/g, "-").toLowerCase()}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-gold/15 text-gold border border-gold/20" : "text-muted-foreground hover:text-white hover:bg-white/5"}`}>
            <n.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3">
        <div className="glass rounded-xl p-4">
          <div className="text-xs text-muted-foreground">Signed in as</div>
          <div className="text-sm text-white font-medium truncate">{user?.business_name || user?.name}</div>
          <button data-testid="sidebar-logout" onClick={doLogout} className="mt-3 flex items-center gap-2 text-xs text-muted-foreground hover:text-destructive transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-3" data-testid="developer-credit-sidebar">
          Developed by{" "}
          <a href="https://instagram.com/moverrotechno" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">MOVERRO TECHNO</a>
          {" "}· @moverrotechno
        </p>
      </div>
    </div>
  );

  return (
    <div className="app-shell min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-black/40 fixed inset-y-0">{SidebarInner}</aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-black border-r border-border">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-40 glass-dark border-b border-border px-4 sm:px-8 py-3.5 flex items-center justify-between">
          <button className="lg:hidden text-white p-1" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu /></button>
          <Link to="/" className="hidden lg:block text-xs text-muted-foreground hover:text-gold transition-colors">← Back to site</Link>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button data-testid="notif-btn" onClick={() => { setShowNotif(!showNotif); }} className="relative w-10 h-10 rounded-full glass flex items-center justify-center text-white hover:border-gold/40">
                <Bell className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                {notifs.unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-gold text-black text-[10px] flex items-center justify-center">{notifs.unread}</span>}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-80 glass-dark rounded-xl p-3 z-50" data-testid="notif-panel">
                  <div className="flex items-center justify-between px-1 mb-2">
                    <span className="text-sm text-white font-medium">Notifications</span>
                    <button onClick={markRead} className="text-xs text-gold hover:underline">Mark all read</button>
                  </div>
                  <div className="max-h-80 overflow-y-auto no-scrollbar space-y-2">
                    {notifs.notifications.length === 0 && <p className="text-sm text-muted-foreground px-1 py-4">No notifications yet.</p>}
                    {notifs.notifications.map((n) => (
                      <div key={n.id} className={`rounded-lg p-3 text-sm ${n.read ? "bg-white/[0.03]" : "bg-gold/10 border border-gold/15"}`}>
                        <p className="text-secondary-foreground">{n.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="w-10 h-10 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center text-gold font-medium">
              {(user?.name || "P").charAt(0).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-8" onClick={() => showNotif && setShowNotif(false)}><Outlet /></main>
      </div>
    </div>
  );
}
